import React, { useMemo } from "react";
import { ReportChartSettingsV2 } from "@/common/report-configuration";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Info } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { LEGEND_POSITIONS } from "@/constants/report";
import { CHART_TYPES_V2 } from "@/common/report-configuration";
import { MEASURES } from "@/common/measures";
import { properCase } from "@/lib/utils";

import {
  ReportDesignerState,
  ReportStateReducerActionType,
} from "@/common/reportDesignerStateReducer";

interface ReportChartConfiguratorProps {
  reportDesignerState: ReportDesignerState;
  reportDesignerDispatch: React.Dispatch<ReportStateReducerActionType>;
}

export const ReportChartConfiguratorV2: React.FC<
  ReportChartConfiguratorProps
> = ({ reportDesignerState: state, reportDesignerDispatch: dispatch }) => {
  const handleChange = (
    field: keyof ReportChartSettingsV2,
    value: string | boolean | number,
  ) => {
    dispatch({ type: "setChartSetting", payload: { field, value } });
  };

  const distinctYAxes = useMemo(() => {
    const axes = new Set<string>(
      state.reportConfig.data.measures.map(
        (measure) => MEASURES[measure].axisGroup,
      ),
    );
    return axes.size;
  }, [state.reportConfig.data.measures]);

  const availableChartTypes = useMemo(() => {
    return Object.entries(CHART_TYPES_V2).filter(([, config]) => {
      // If the chart type restricts to specific datasets, enforce that
      if (config.restrictToDatasets) {
        return config.restrictToDatasets.includes(
          state.reportConfig.data.datasetName,
        );
      }
      return true;
    });
  }, [state.reportConfig.data.datasetName]);

  return (
    <div className="space-y-4 p-4">
      <div className="space-y-4">
        {/* Chart Type */}
        <div className="space-y-2">
          <h3 className="font-semibold">Chart Type</h3>
          <Select
            value={state.reportConfig.chart.chartType}
            onValueChange={(value) => handleChange("chartType", value)}
          >
            <SelectTrigger id="chartType" className="w-full border rounded-md">
              <SelectValue placeholder="Select chart type" />
            </SelectTrigger>
            <SelectContent>
              {availableChartTypes.map(([key, config]) => (
                <SelectItem key={key} value={config.label}>
                  {config.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Display Limit */}
        <div className="space-y-2">
          <h3 className="font-semibold">Display Limit</h3>
          <Input
            id="limit"
            type="number"
            min={1}
            max={100}
            value={state.reportConfig.chart.limit}
            onChange={(e) => {
              const value = e.target.value;
              const parsedValue = parseInt(value, 10);
              handleChange(
                "limit",
                value === "" ? "" : Math.max(1, parsedValue || 1),
              );
            }}
            className="w-full border rounded-md"
            placeholder="Enter display limit"
          />
        </div>

        {/* Axis Labels */}
        <div className="space-y-2">
          <h3 className="font-semibold">X-Axis Label</h3>
          <Input
            id="xAxisLabel"
            value={state.reportConfig.chart.xAxisLabelOverride}
            onChange={(e) => handleChange("xAxisLabelOverride", e.target.value)}
            placeholder="Enter x-axis label"
            className="w-full border rounded-md"
          />
        </div>

        <div className="space-y-2">
          <h3 className="font-semibold">Left Y-Axis Label</h3>
          <Input
            id="yAxisLabel"
            value={state.reportConfig.chart.yAxisLabelOverride1}
            onChange={(e) =>
              handleChange("yAxisLabelOverride1", e.target.value)
            }
            placeholder="Enter y-axis label"
            className="w-full border rounded-md"
          />
        </div>

        {distinctYAxes > 1 && (
          <div className="space-y-2">
            <h3 className="font-semibold">Right Y-Axis Label</h3>
            <Input
              id="yAxisLabel2"
              value={state.reportConfig.chart.yAxisLabelOverride2}
              onChange={(e) =>
                handleChange("yAxisLabelOverride2", e.target.value)
              }
              placeholder="Enter right y-axis label"
              className="w-full border rounded-md"
            />
          </div>
        )}

        {/* Legend Controls */}
        <div className="space-y-2">
          <h3 className="font-semibold">Include Legend</h3>
          <div className="flex items-center">
            <span className="mr-2">Off</span>
            <Switch
              className="my-2"
              id="includeLegend"
              checked={state.reportConfig.chart.includeLegend}
              onCheckedChange={(checked) =>
                handleChange("includeLegend", checked)
              }
            />
            <span className="ml-2">On</span>
          </div>

          {state.reportConfig.chart.includeLegend && (
            <div className="space-y-2">
              <h3 className="font-semibold">Legend Position</h3>
              <Select
                value={state.reportConfig.chart.legendPosition}
                onValueChange={(value) => handleChange("legendPosition", value)}
              >
                <SelectTrigger
                  id="legendPosition"
                  className="w-full border rounded-md"
                >
                  <SelectValue placeholder="Select legend position" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(LEGEND_POSITIONS).map(([key, value]) => (
                    <SelectItem key={key} value={value}>
                      {properCase(value)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        {/* Hide Small Groups Toggle */}
        <div className="space-y-2">
          <div className="flex items-center space-x-2">
            <h3 className="font-semibold">Hide Small Groups</h3>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="h-4 w-4 text-muted-foreground" />
                </TooltipTrigger>
                <TooltipContent className="w-[300px] p-4">
                  <p>
                    When enabled, this option hides data points associated with
                    groups that contain 5 or fewer employees.
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <div className="flex items-center">
            <span className="mr-2">Off</span>
            <Switch
              className="my-2"
              id="hideSmallGroups"
              checked={state.reportConfig.chart.hideSmallGroups || false}
              onCheckedChange={(checked) =>
                handleChange("hideSmallGroups", checked)
              }
            />
            <span className="ml-2">On</span>
          </div>
        </div>
      </div>
    </div>
  );
};
